import type { FileAsset } from "@nlc/shared-types";
import type { Path } from "react-hook-form";

import { UploadIcon } from "lucide-react";
import { useState } from "react";

import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types.ts";

import { MediaPreviewModal } from "@/components/media-preview-modal/index.tsx";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess.tsx";
import { useDownloadFile } from "@/features/my-files/hooks/useDownloadFile";
import { CollapsibleBox } from "@/features/request/edit/components/CollapsibleBox.tsx";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm.tsx";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";
import { mapAttachmentsToAssets } from "@/features/request/edit/utils/utils.ts";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import type { RequestFormData } from "./types.ts";

import { useGetAttachments } from "../../hooks/useGetAttachments.tsx";
import { AssetDownloadSection } from "./components/AssetsDownloadButtons.tsx";
import { AssetsForm } from "./components/AssetsForm.tsx";
import { isFileAsset } from "./utils/uploadUtils.ts";

export function AssetsSection<TFormValues extends RequestFormData>() {
  const formMethods = useEditRequestFormContext();
  const { canAccess } = useRoleAccess();

  const { isLocked } = useTemplateLock();
  const isReadOnly =
    canAccess("edit-request.details.designer.view") || isLocked;

  return (
    <CollapsibleBox
      icon={<UploadIcon className="text-white" size={16} />}
      iconClassName="bg-blue-900"
      title="Assets"
    >
      {isReadOnly ? (
        <ReadOnlyAssets />
      ) : (
        <AssetsForm
          field={"assets" as Path<TFormValues>}
          formMethods={formMethods}
          title="Assets"
        />
      )}
    </CollapsibleBox>
  );
}

function ReadOnlyAssets() {
  const { data: attachments } = useGetAttachments();
  const { assets } = mapAttachmentsToAssets(attachments || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>();
  const { mutate: downloadFile } = useDownloadFile();

  const fileAssets = assets.filter((element): element is FileAsset =>
    isFileAsset(element),
  );

  const assetMediaItems: MediaItem[] = fileAssets.map((asset) => ({
    extension: getExtensionFromFilename(asset.file.name),
    id: asset.id,
    name: asset.file.name,
    url: asset.awsDownloadUrl || "",
  }));

  const handleAssetClick = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDownloadAsset = (item: MediaItem) => {
    if (item.id) {
      const asset = fileAssets.find((a) => a.id === item.id);

      if (asset?.attachmentId) {
        downloadFile(asset.attachmentId);
      }
    }
  };

  return (
    <>
      <AssetDownloadSection
        assets={assets}
        emptyMessage="No assets available"
        onAssetClick={handleAssetClick}
        title="Assets"
      />

      {isModalOpen && (
        <MediaPreviewModal
          alt=""
          currentItemId={selectedAssetId}
          mediaItems={assetMediaItems}
          onClose={handleModalClose}
          onDownload={handleDownloadAsset}
        />
      )}
    </>
  );
}
